/**
 * Connectivity state (Req 28.4, design "Clients → Operator_Console"): the console counts as
 * online only while `navigator.onLine` is true AND the last health ping succeeded. A
 * captive portal or a down API therefore also disables invoice and redemption actions.
 *
 * The ping is `GET <healthUrl>` (unauthenticated liveness probe, same origin, `no-store`)
 * with a timeout. It runs on start, every `intervalMs` while the page is visible, on the
 * browser `online` event and when the page becomes visible again. Going offline in the
 * browser marks the state offline immediately without waiting for a ping.
 *
 * `onReconnect` fires on every offline → online transition: the seam where the offline scan
 * queue flushes (see services.ts).
 */
export type ApiReachability = 'unknown' | 'reachable' | 'unreachable';

export interface ConnectivityState {
  /** `navigator.onLine`. */
  browserOnline: boolean;
  /** Result of the last health ping. */
  api: ApiReachability;
  /** Derived: browser online and the API not known to be unreachable. */
  online: boolean;
}

export interface Connectivity {
  get(): ConnectivityState;
  subscribe(listener: () => void): () => void;
  /** Pings now (concurrent calls share one ping) and returns the new state. */
  check(): Promise<ConnectivityState>;
  onReconnect(listener: () => void): () => void;
  start(): void;
  stop(): void;
}

/** The part of `window` / `document` the monitor listens on. */
export interface EventSourceLike {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface ConnectivityOptions {
  healthUrl: string;
  fetch?: typeof globalThis.fetch;
  intervalMs?: number;
  timeoutMs?: number;
  /** Event source for `online`/`offline`/`visibilitychange` (default `window`/`document`). */
  target?: EventSourceLike;
  doc?: EventSourceLike & { readonly visibilityState: DocumentVisibilityState };
  isBrowserOnline?: () => boolean;
}

function derive(browserOnline: boolean, api: ApiReachability): ConnectivityState {
  return { browserOnline, api, online: browserOnline && api !== 'unreachable' };
}

export function createConnectivity(opts: ConnectivityOptions): Connectivity {
  const doFetch = opts.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const intervalMs = opts.intervalMs ?? 30_000;
  const timeoutMs = opts.timeoutMs ?? 5_000;
  const target = opts.target ?? window;
  const doc = opts.doc ?? document;
  const isBrowserOnline = opts.isBrowserOnline ?? (() => navigator.onLine);

  let state = derive(isBrowserOnline(), 'unknown');
  const listeners = new Set<() => void>();
  const reconnectListeners = new Set<() => void>();
  let pending: Promise<ConnectivityState> | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;

  const set = (browserOnline: boolean, api: ApiReachability) => {
    const next = derive(browserOnline, api);
    if (next.browserOnline === state.browserOnline && next.api === state.api) return;
    const wasOnline = state.online;
    state = next;
    for (const l of listeners) l();
    if (!wasOnline && next.online && next.api === 'reachable') for (const l of reconnectListeners) l();
  };

  async function ping(): Promise<ApiReachability> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await doFetch(opts.healthUrl, { cache: 'no-store', signal: controller.signal });
      return res.ok ? 'reachable' : 'unreachable';
    } catch {
      return 'unreachable';
    } finally {
      clearTimeout(timeout);
    }
  }

  function check(): Promise<ConnectivityState> {
    if (!isBrowserOnline()) {
      set(false, state.api);
      return Promise.resolve(state);
    }
    pending ??= ping()
      .then((api) => {
        set(isBrowserOnline(), api);
        return state;
      })
      .finally(() => {
        pending = undefined;
      });
    return pending;
  }

  const onOnline = () => void check();
  const onOffline = () => set(false, state.api);
  const onVisibility = () => {
    if (doc.visibilityState === 'visible') void check();
  };

  return {
    get: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    check,
    onReconnect(listener) {
      reconnectListeners.add(listener);
      return () => reconnectListeners.delete(listener);
    },
    start() {
      if (timer !== undefined) return;
      target.addEventListener('online', onOnline);
      target.addEventListener('offline', onOffline);
      doc.addEventListener('visibilitychange', onVisibility);
      timer = setInterval(() => {
        if (doc.visibilityState === 'visible') void check();
      }, intervalMs);
      void check();
    },
    stop() {
      if (timer === undefined) return;
      clearInterval(timer);
      timer = undefined;
      target.removeEventListener('online', onOnline);
      target.removeEventListener('offline', onOffline);
      doc.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
